import { UuidGenerator, assert } from '@standardnotes/utils';
import { KeySystemRootKeyStorageMode, } from '@standardnotes/models';
import { Result } from '@standardnotes/domain-core';
export class RotateVaultKey {
    constructor(mutator, encryption, keys, _notifyVaultUsersOfKeyRotation, _isVaultOwner) {
        this.mutator = mutator;
        this.encryption = encryption;
        this.keys = keys;
        this._notifyVaultUsersOfKeyRotation = _notifyVaultUsersOfKeyRotation;
        this._isVaultOwner = _isVaultOwner;
    }
    async execute(params) {
        const { newRootKey, updatedVault } = await this.updateRootKeyparams(params);
        await this.createNewKeySystemItemsKey({
            keySystemIdentifier: updatedVault.systemIdentifier,
            sharedVaultUuid: updatedVault.isSharedVaultListing() ? updatedVault.sharing.sharedVaultUuid : undefined,
            rootKeyToken: newRootKey.token,
        });
        await this.keys.queueVaultItemsKeysForReencryption(updatedVault.systemIdentifier);
        const shareResult = await this.shareNewKeyWithMembers({
            vault: updatedVault,
            newRootKey,
        });
        if (shareResult.isFailed()) {
            return Result.fail(shareResult.getError());
        }
        return Result.ok(updatedVault);
    }
    async updateRootKeyparams(params) {
        const currentRootKey = this.keys.getPrimaryKeySystemRootKey(params.vault.systemIdentifier);
        if (!currentRootKey) {
            throw new Error('Cannot rotate key system root key; key system root key not found');
        }
        let newRootKey;
        if (params.userInputtedPassword) {
            newRootKey = this.encryption.createUserInputtedKeySystemRootKey({
                systemIdentifier: params.vault.systemIdentifier,
                userInputtedPassword: params.userInputtedPassword,
            });
        }
        else {
            newRootKey = this.encryption.createRandomizedKeySystemRootKey({
                systemIdentifier: params.vault.systemIdentifier,
            });
        }
        if (!newRootKey) {
            throw new Error('Cannot rotate key system root key; new root key not created');
        }
        if (!params.userInputtedPassword || params.vault.keyStorageMode === KeySystemRootKeyStorageMode.Synced) {
            await this.mutator.insertItem(newRootKey, true);
        }
        else {
            this.keys.cacheKey(newRootKey, params.vault.keyStorageMode);
        }
        const updatedVault = await this.mutator.changeItem(params.vault, (mutator) => {
            assert(newRootKey);
            mutator.rootKeyParams = newRootKey.keyParams;
        });
        return { newRootKey, updatedVault };
    }
    async createNewKeySystemItemsKey(params) {
        const newItemsKeyUuid = UuidGenerator.GenerateUuid();
        const newItemsKey = this.encryption.createKeySystemItemsKey(newItemsKeyUuid, params.keySystemIdentifier, params.sharedVaultUuid, params.rootKeyToken);
        await this.mutator.insertItem(newItemsKey);
    }
    async shareNewKeyWithMembers(params) {
        if (!params.vault.isSharedVaultListing()) {
            return Result.ok();
        }
        const isOwner = this._isVaultOwner.execute(params.vault).getValue();
        if (!isOwner) {
            return Result.ok();
        }
        const result = await this._notifyVaultUsersOfKeyRotation.execute({
            sharedVault: params.vault,
        });
        return result;
    }
}
